import classNames from 'classnames'
import { UserIcon } from '@heroiconsv2/react/24/outline'
import { includes, toLower } from 'lodash'
import DocumentMagicLink from 'components/molecules/DocumentMagicLink'
import { LABEL, PANEL, ROW_START, SectionCard } from './BookingSection'

const BookingUserDetailsCard = ({
  bookingData,
  t,
  hasAdminAccess,
  canViewUserDocuments = hasAdminAccess,
  onUpdateDocumentVerification,
  onOpenDocumentPreview,
  onOpenFullscreenImage,
}) => (
  <SectionCard icon={UserIcon} title={t('booking.sections.userDetails')}>
    <div>
      <div className={PANEL}>
        <div className={ROW_START}>
          <div className={LABEL}>{t('booking.labels.name')}</div>
          <div className="text-sm sm:text-base text-slate-800 sm:text-right break-words">{bookingData?.user?.fullName || '-'}</div>
        </div>

        <div className={ROW_START}>
          <div className={LABEL}>{t('booking.labels.email')}</div>
          <div className="text-sm sm:text-base text-slate-800 sm:text-right break-all">{bookingData?.user?.email || '-'}</div>
        </div>

        {(hasAdminAccess || bookingData?.status === 'confirmed' || bookingData?.status === 'completed') && (
          <div className={ROW_START}>
            <div className={LABEL}>{t('booking.labels.phone')}</div>
            <div className="text-sm sm:text-base text-slate-800 sm:text-right">{bookingData?.user?.phone || '-'}</div>
          </div>
        )}

        {canViewUserDocuments && (bookingData?.user?.idCard || bookingData?.user?.drivingLicense || bookingData?.user?.passport) && (
          <div className="mt-4 border-t border-slate-200/80 pt-4">
            <div className={classNames(LABEL, 'mb-3')}>{t('booking.sections.userDocuments')}</div>
            <div className="flex flex-wrap gap-3">
              {bookingData?.user?.idCard && (
                <div className="flex flex-col items-center">
                  <div
                    className="relative cursor-pointer overflow-hidden rounded-xl ring-1 ring-slate-300/90 transition-all hover:ring-2 hover:ring-sky-400/80 hover:shadow-md"
                    onClick={() => {
                      if (includes(toLower(bookingData.user.idCard), '.pdf')) {
                        onOpenDocumentPreview(bookingData.user.idCard, t('userProfile.form.idCard'), 'pdf')
                      } else {
                        onOpenFullscreenImage(bookingData.user.idCard, t('userProfile.form.idCard'))
                      }
                    }}
                  >
                    {includes(toLower(bookingData.user.idCard), '.pdf') ? (
                      <div className="h-32 w-48 flex items-center justify-center bg-blue-50 border-2 border-blue-200 rounded-lg">
                        <div className="text-center">
                          <div className="text-blue-600 text-sm font-medium">PDF</div>
                          <div className="text-blue-500 text-xs">Click to preview</div>
                        </div>
                      </div>
                    ) : (
                      <img
                        src={bookingData.user.idCard}
                        alt="ID Card Preview"
                        className="h-32 w-48 object-cover hover:opacity-90 transition-opacity"
                        onError={e => {
                          e.target.style.display = 'none'
                          e.target.nextSibling.style.display = 'flex'
                        }}
                      />
                    )}
                    <div style={{ display: 'none' }} className="h-32 w-48 flex items-center justify-center bg-gray-100">
                      <span className="text-gray-500 text-sm">{t('userProfile.modal.imageNotAvailable')}</span>
                    </div>
                  </div>
                  <div className="mt-2 text-xs text-center text-gray-600">{t('userProfile.form.idCard')}</div>
                  <div className="flex gap-1 mt-1">
                    <button
                      type="button"
                      onClick={() => onUpdateDocumentVerification('idCard', true)}
                      className={`px-2 py-1 rounded text-xs font-medium ${
                        bookingData?.user?.isIdCardVerified === true ? 'bg-dark-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-green-100'
                      }`}
                    >
                      {t('userProfile.buttons.accept')}
                    </button>
                    <button
                      type="button"
                      onClick={() => onUpdateDocumentVerification('idCard', false)}
                      className={`px-2 py-1 rounded text-xs font-medium ${
                        bookingData?.user?.isIdCardRejected === true ? 'bg-red-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-red-100'
                      }`}
                    >
                      {t('userProfile.buttons.reject')}
                    </button>
                  </div>
                </div>
              )}

              {bookingData?.user?.drivingLicense && (
                <div className="flex flex-col items-center">
                  <div
                    className="relative cursor-pointer overflow-hidden rounded-xl ring-1 ring-slate-300/90 transition-all hover:ring-2 hover:ring-sky-400/80 hover:shadow-md"
                    onClick={() => {
                      if (includes(toLower(bookingData.user.drivingLicense), '.pdf')) {
                        onOpenDocumentPreview(bookingData.user.drivingLicense, t('userProfile.form.drivingLicense'), 'pdf')
                      } else {
                        onOpenFullscreenImage(bookingData.user.drivingLicense, t('userProfile.form.drivingLicense'))
                      }
                    }}
                  >
                    {includes(toLower(bookingData.user.drivingLicense), '.pdf') ? (
                      <div className="h-32 w-48 flex items-center justify-center bg-blue-50 border-2 border-blue-200 rounded-lg">
                        <div className="text-center">
                          <div className="text-blue-600 text-sm font-medium">PDF</div>
                          <div className="text-blue-500 text-xs">Click to preview</div>
                        </div>
                      </div>
                    ) : (
                      <img
                        src={bookingData.user.drivingLicense}
                        alt="Driving License Preview"
                        className="h-32 w-48 object-cover hover:opacity-90 transition-opacity"
                        onError={e => {
                          e.target.style.display = 'none'
                          e.target.nextSibling.style.display = 'flex'
                        }}
                      />
                    )}
                    <div style={{ display: 'none' }} className="h-32 w-48 flex items-center justify-center bg-gray-100">
                      <span className="text-gray-500 text-sm">{t('userProfile.modal.imageNotAvailable')}</span>
                    </div>
                  </div>
                  <div className="mt-2 text-xs text-center text-gray-600">{t('userProfile.form.drivingLicense')}</div>
                  <div className="flex gap-1 mt-1">
                    <button
                      type="button"
                      onClick={() => onUpdateDocumentVerification('drivingLicense', true)}
                      className={`px-2 py-1 rounded text-xs font-medium ${
                        bookingData?.user?.isDrivingLicenseVerified === true ? 'bg-dark-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-green-100'
                      }`}
                    >
                      {t('userProfile.buttons.accept')}
                    </button>
                    <button
                      type="button"
                      onClick={() => onUpdateDocumentVerification('drivingLicense', false)}
                      className={`px-2 py-1 rounded text-xs font-medium ${
                        bookingData?.user?.isDrivingLicenseRejected === true ? 'bg-red-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-red-100'
                      }`}
                    >
                      {t('userProfile.buttons.reject')}
                    </button>
                  </div>
                </div>
              )}

              {bookingData?.user?.passport && (
                <div className="flex flex-col items-center">
                  <div
                    className="relative cursor-pointer overflow-hidden rounded-xl ring-1 ring-slate-300/90 transition-all hover:ring-2 hover:ring-sky-400/80 hover:shadow-md"
                    onClick={() => {
                      if (includes(toLower(bookingData.user.passport), '.pdf')) {
                        onOpenDocumentPreview(bookingData.user.passport, t('userProfile.form.passport'), 'pdf')
                      } else {
                        onOpenFullscreenImage(bookingData.user.passport, t('userProfile.form.passport'))
                      }
                    }}
                  >
                    {includes(toLower(bookingData.user.passport), '.pdf') ? (
                      <div className="h-32 w-48 flex items-center justify-center bg-blue-50 border-2 border-blue-200 rounded-lg">
                        <div className="text-center">
                          <div className="text-blue-600 text-sm font-medium">PDF</div>
                          <div className="text-blue-500 text-xs">Click to preview</div>
                        </div>
                      </div>
                    ) : (
                      <img
                        src={bookingData.user.passport}
                        alt="Passport Preview"
                        className="h-32 w-48 object-cover hover:opacity-90 transition-opacity"
                        onError={e => {
                          e.target.style.display = 'none'
                          e.target.nextSibling.style.display = 'flex'
                        }}
                      />
                    )}
                    <div style={{ display: 'none' }} className="h-32 w-48 flex items-center justify-center bg-gray-100">
                      <span className="text-gray-500 text-sm">{t('userProfile.modal.imageNotAvailable')}</span>
                    </div>
                  </div>
                  <div className="mt-2 text-xs text-center text-gray-600">{t('userProfile.form.passport')}</div>
                  <div className="flex gap-1 mt-1">
                    <button
                      type="button"
                      onClick={() => onUpdateDocumentVerification('passport', true)}
                      className={`px-2 py-1 rounded text-xs font-medium ${
                        bookingData?.user?.isPassportVerified === true ? 'bg-dark-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-green-100'
                      }`}
                    >
                      {t('userProfile.buttons.accept')}
                    </button>
                    <button
                      type="button"
                      onClick={() => onUpdateDocumentVerification('passport', false)}
                      className={`px-2 py-1 rounded text-xs font-medium ${
                        bookingData?.user?.isPassportRejected === true ? 'bg-red-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-red-100'
                      }`}
                    >
                      {t('userProfile.buttons.reject')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {hasAdminAccess && (
          <div className="mt-4 border-t border-slate-200/80 pt-4">
            <DocumentMagicLink user={bookingData?.user} userId={bookingData?.user?._id} bookingId={bookingData?._id} />
          </div>
        )}
      </div>
    </div>
  </SectionCard>
)

export default BookingUserDetailsCard
